import type { LucideIcon } from 'lucide-react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { painelDescriptionClass } from '@/components/painel/painel-styles';

export type PainelStatVariant = 'sky' | 'violet' | 'cyan' | 'indigo';

type PainelStatCardProps = {
  title: string;
  value: string;
  icon: LucideIcon;
  description?: string;
  trend?: {
    label: string;
    positive?: boolean;
  };
  variant?: PainelStatVariant;
};

const variantMap: Record<
  PainelStatVariant,
  {
    glow: string;
    iconShell: string;
    iconColor: string;
  }
> = {
  sky: {
    glow: 'shadow-[0_0_48px_-14px_rgba(56,189,248,0.35)]',
    iconShell: 'bg-gradient-to-br from-sky-500/25 to-sky-600/5 ring-sky-400/35',
    iconColor: 'text-sky-200',
  },
  violet: {
    glow: 'shadow-[0_0_48px_-14px_rgba(167,139,250,0.28)]',
    iconShell: 'bg-gradient-to-br from-violet-500/20 to-violet-700/5 ring-violet-400/25',
    iconColor: 'text-violet-200',
  },
  cyan: {
    glow: 'shadow-[0_0_48px_-14px_rgba(34,211,238,0.28)]',
    iconShell: 'bg-gradient-to-br from-cyan-500/20 to-cyan-700/5 ring-cyan-400/25',
    iconColor: 'text-cyan-200',
  },
  indigo: {
    glow: 'shadow-[0_0_48px_-14px_rgba(129,140,248,0.28)]',
    iconShell: 'bg-gradient-to-br from-indigo-500/20 to-indigo-800/5 ring-indigo-400/25',
    iconColor: 'text-indigo-200',
  },
};

export function PainelStatCard({
  title,
  value,
  icon: Icon,
  description,
  trend,
  variant = 'sky',
}: PainelStatCardProps) {
  const v = variantMap[variant];

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-2xl border border-white/10 bg-blue-950/45 p-6 backdrop-blur-md',
        'transition-all duration-300 ease-out',
        'hover:-translate-y-0.5 hover:border-sky-400/40 hover:bg-blue-950/60',
        v.glow,
        'hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.45)]'
      )}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-sky-500/[0.04] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-200/55">
            {title}
          </p>
          <p className="mt-3 font-poppins text-3xl font-bold tabular-nums tracking-tight text-white md:text-[2.1rem]">
            {value}
          </p>
          {description ? (
            <p className={cn('mt-2 font-inter text-xs leading-relaxed', painelDescriptionClass)}>{description}</p>
          ) : null}
          {trend ? (
            <div
              className={cn(
                'mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-poppins text-[11px] font-semibold',
                trend.positive === false
                  ? 'bg-rose-500/15 text-rose-200/90 ring-1 ring-rose-400/25'
                  : 'bg-emerald-500/15 text-emerald-200/90 ring-1 ring-emerald-400/25'
              )}
            >
              {trend.positive === false ? (
                <TrendingDown className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <TrendingUp className="h-3.5 w-3.5 shrink-0" />
              )}
              {trend.label}
            </div>
          ) : null}
        </div>
        <div
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ring-1 shadow-inner transition-transform duration-300 group-hover:scale-105',
            v.iconShell
          )}
        >
          <Icon className={cn('h-6 w-6', v.iconColor)} />
        </div>
      </div>
    </div>
  );
}
